import React from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider } from "@auth0/auth0-react";
import App from "@/App.jsx";
import "@/index.css";
import { resolveAuthUrl } from "@/lib/auth/auth0Urls";
import { Sentry, initSentry } from "@/lib/observability/sentry";

const domain = (import.meta.env.VITE_AUTH0_DOMAIN || "").trim();
const clientId = (import.meta.env.VITE_AUTH0_CLIENT_ID || "").trim();
const audience = (import.meta.env.VITE_AUTH0_AUDIENCE || "").trim();
const hasAuth0Config = Boolean(domain && clientId);
const devBypassEnabled = String(
  import.meta.env.VITE_DEV_AUTH_BYPASS ?? (import.meta.env.DEV ? "true" : "false")
).toLowerCase() === "true";
initSentry();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const MissingAuth0Config = () => (
  <div className="auth0-config-error">
    <div className="auth0-config-error-card">
      <h1>Auth0 Configuration Required</h1>
      <p>The frontend is missing required Auth0 environment variables.</p>
      <p>
        Required: <code>VITE_AUTH0_DOMAIN</code>, <code>VITE_AUTH0_CLIENT_ID</code>
      </p>
      <p>
        For local bypass, set <code>VITE_DEV_AUTH_BYPASS=true</code>.
      </p>
    </div>
  </div>
);

const onRedirectCallback = (appState) => {
  const returnTo = appState?.returnTo;
  if (typeof returnTo === "string" && returnTo.length > 0) {
    window.history.replaceState({}, document.title, returnTo);
  }
};

const appTree = (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please reload the page.</p>}>
    <React.StrictMode>
      <App />
    </React.StrictMode>
  </Sentry.ErrorBoundary>
);

if (!hasAuth0Config && !devBypassEnabled) {
  console.error("Auth0 configuration missing. Please set VITE_AUTH0_DOMAIN and VITE_AUTH0_CLIENT_ID.");
  ReactDOM.createRoot(rootElement).render(<MissingAuth0Config />);
} else if (hasAuth0Config) {
  const redirectUri = resolveAuthUrl(
    import.meta.env.VITE_AUTH_REDIRECT_URI,
    "/",
  );
  const authorizationParams = {
    redirect_uri: redirectUri,
    ...(audience ? { audience } : {}),
  };

  ReactDOM.createRoot(rootElement).render(
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={authorizationParams}
      onRedirectCallback={onRedirectCallback}
    >
      {appTree}
    </Auth0Provider>
  );
} else {
  console.warn("Auth0 not configured. Running with dev auth bypass path.");
  ReactDOM.createRoot(rootElement).render(appTree);
}
